import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/api';
import DetailPage from '../../components/DetailPage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/detail.css';

export default function AddDataDoc() {
  const { datasetId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]); // [{label,type,options,value}]
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const ds = await apiFetch(`/datasets/getDataSetById/${datasetId}`, { method: 'GET' });
        const tplId = ds?.templateId?._id || ds?.templateId;
        if (!tplId) {
          toast.error('Dataset has no template.');
          setLoading(false);
          return;
        }
        const tpl = await apiFetch(`/templates/getTemplateById/${tplId}`, { method: 'GET' });
        setTemplate(tpl || null);

        const list = (tpl?.fields || []).map((f) => ({
          label: f?.label || 'Field',
          type: (f?.type || 'text').toLowerCase(),
          options: Array.isArray(f?.options) ? f.options : [],
          value: '',
        }));
        setFields(list);
      } catch (e) {
        console.error('Load template error', e);
        toast.error('Failed to load template.');
      } finally {
        setLoading(false);
      }
    })();
  }, [datasetId]);

  const updateValue = (idx, val) => {
    setFields((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], value: val };
      return next;
    });
  };

  const handleSave = async () => {
    if (!template) return;
    try {
      setSaving(true);
      const payload = {
        datasetId,
        templateId: template._id,
        orgId: localStorage.getItem('activeOrgId'),
       };
      fields.forEach((f, i) => {
        payload[`fld${i + 1}`] = f.value ?? '';
      });

      const res = await apiFetch('/datadoc/addDataDoc', { method: 'POST', body: payload });

      if (res && res._id) {
        toast.success('Data document saved!');
        setTimeout(() => navigate(`/datasets/viewdataset/${datasetId}`), 600);
      } else {
        toast.error(res?.message || 'Save failed.');
      }
    } catch (e) {
      console.error('Save error', e);
      toast.error(e?.message || 'Network error.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DetailPage
        title="Add Data Document"
        backTo={`/datasets/viewdataset/${datasetId}`}
        backLabel="Back to Data Set"
      >
        <ToastContainer position="top-right" />
        <div>Loading…</div>
      </DetailPage>
    );
  }

  return (
    <DetailPage
      title="Add Data Document"
      backTo={`/datasets/viewdataset/${datasetId}`}
      backLabel="Back to Data Set"
      meta={
        template?.templateName ? (
          <>
            Template: <strong>{template.templateName}</strong>
          </>
        ) : null
      }
    >
      <ToastContainer position="top-right" />

      <div className="detail-card">
        <div className="form-rows">
          {fields.map((f, idx) => {
            const id = `f_${idx}`;
            const label = f.label || `Field ${idx + 1}`;

            if (f.type === 'dropdown') {
              return (
                <div key={id} className="form-row">
                  <label htmlFor={id}>{label}</label>
                  <select id={id} value={f.value} onChange={(e) => updateValue(idx, e.target.value)}>
                    <option value="">Select…</option>
                    {f.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }

            if (f.type === 'checkbox') {
              return (
                <div key={id} className="form-row">
                  <label htmlFor={id}>{label}</label>
                  <input
                    id={id}
                    type="checkbox"
                    checked={!!f.value}
                    onChange={(e) => updateValue(idx, e.target.checked ? 'true' : '')}
                  />
                </div>
              );
            }

            if (f.type === 'radio') {
              return (
                <div key={id} className="form-row">
                  <label>{label}</label>
                  <div>
                    {(f.options || []).map((opt) => {
                      const rid = `${id}_${opt}`;
                      return (
                        <label key={rid} htmlFor={rid} style={{ marginRight: 12 }}>
                          <input
                            id={rid}
                            type="radio"
                            name={id}
                            value={opt}
                            checked={f.value === opt}
                            onChange={(e) => updateValue(idx, e.target.value)}
                          />{' '}
                          {opt}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            }

            // default: text
            return (
              <div key={id} className="form-row">
                <label htmlFor={id}>{label}</label>
                <input
                  id={id}
                  type="text"
                  value={f.value}
                  onChange={(e) => updateValue(idx, e.target.value)}
                  placeholder={label}
                />
              </div>
            );
          })}
        </div>

        <div className="actions">
          <button className="btn btn-success" onClick={handleSave} disabled={saving || !template}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            className="btn btn-cancel"
            onClick={() => navigate(`/datasets/viewdataset/${datasetId}`)}
          >
            Cancel
          </button>
        </div>
      </div>
    </DetailPage>
  );
}
